import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { ItemSobrante } from '@/lib/supabase/tipos-bd'
import { useSesion } from '@/modules/auth/useSesion'
import { partirDescripcion } from '@/modules/cotizador/useCotizador'
import { fechaHora } from '@/shared/utilidades/formato'

import { ElegirSobrantes } from './ElegirSobrantes'
import { FotosDelSobrante } from './FotosDelSobrante'
import {
  cantidadLegible,
  useMaterialesDeLaOrden,
  useReportarSobrantes,
  useSobrantesDeLaOrden,
} from './useSobrantes'
import estilos from './SobrantesDeLaOrden.module.css'

type Props = {
  ordenTrabajoId: string
  /** Si la orden ya terminó: solo entonces se pregunta qué sobró. */
  terminada: boolean
}

/**
 * El material que sobró, en el precierre de la orden (0310).
 *
 * El técnico lo dice al terminar. Si terminó antes de que existiera la
 * pregunta, o con una versión anterior de la app, aquí lo registra la oficina.
 */
export function SobrantesDeLaOrden({ ordenTrabajoId, terminada }: Props) {
  const { puede } = useSesion()
  const { data, isLoading } = useSobrantesDeLaOrden(ordenTrabajoId)
  const [registrando, setRegistrando] = useState(false)

  if (isLoading || !data) return null
  const asignados = data.asignados ?? []
  if (!terminada && asignados.length === 0) return null

  const pendientes = data.items.filter((i) => i.estado === 'por_verificar').length

  return (
    <section className={estilos.zona}>
      <header className={estilos.cabecera}>
        <h3 className={estilos.titulo}>
          Material sobrante
          {pendientes > 0 && <span className={estilos.cuenta}>{pendientes}</span>}
        </h3>
        {data.reportados_en && (
          <p className={estilos.pie}>
            Lo reportó {data.reportados_por ?? 'alguien'} · {fechaHora(data.reportados_en)}
          </p>
        )}
      </header>

      {/* 0314: lo que se le asignó del inventario de sobrantes. */}
      {asignados.length > 0 && (
        <div className={estilos.asignados}>
          <p className={estilos.subtitulo}>Lleva del inventario de sobrantes</p>
          <ul className={estilos.lista}>
            {asignados.map((a) => (
              <li key={a.id_inventario} className={estilos.filaAsignado}>
                <span className={estilos.nombre}>{partirDescripcion(a.descripcion).nombre}</span>
                <span className={estilos.cantidad}>
                  {cantidadLegible(Number(a.cantidad))} {a.unidad ?? ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!terminada ? null : !data.reportados_en ? (
        registrando ? (
          <Registrar ordenTrabajoId={ordenTrabajoId} onListo={() => setRegistrando(false)} />
        ) : (
          <div className={estilos.sinReporte}>
            <p>El técnico no dijo si sobró material (terminó antes de esta pregunta o con una versión anterior de la app).</p>
            {puede('sobrantes', 'crear') && (
              <button type="button" className={estilos.boton} onClick={() => setRegistrando(true)}>
                Registrar lo que sobró
              </button>
            )}
          </div>
        )
      ) : data.items.length === 0 ? (
        <p className={estilos.nada}>No sobró material.</p>
      ) : (
        <>
          <ul className={estilos.lista}>
            {data.items.map((i) => {
              const { nombre } = partirDescripcion(i.descripcion)
              return (
                <li key={i.id} className={estilos.fila}>
                  <span className={estilos.nombre}>{nombre}</span>
                  <span className={estilos.cantidad}>
                    {cantidadLegible(i.cantidad_reportada)} {i.unidad ?? ''}
                  </span>
                  <span className={`${estilos.estado} ${estilos[i.estado]}`}>
                    {i.estado === 'por_verificar'
                      ? 'Por verificar'
                      : i.estado === 'verificado'
                        ? `Entró ${cantidadLegible(i.cantidad_verificada ?? 0)}`
                        : 'No llegó'}
                  </span>
                </li>
              )
            })}
          </ul>
          {data.fotos?.length > 0 && <FotosDelSobrante rutas={data.fotos} />}
          {pendientes > 0 && puede('sobrantes', 'ver') && (
            <Link to="/sobrantes" className={estilos.enlace}>
              Verificar en el inventario de sobrantes →
            </Link>
          )}
        </>
      )}
    </section>
  )
}

function Registrar({ ordenTrabajoId, onListo }: { ordenTrabajoId: string; onListo: () => void }) {
  const materiales = useMaterialesDeLaOrden(ordenTrabajoId)
  const reportar = useReportarSobrantes()
  const [valor, setValor] = useState<ItemSobrante[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function guardar() {
    if (valor === null) return
    setError(null)
    try {
      await reportar.mutateAsync({ ordenTrabajoId, items: valor })
      onListo()
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo guardar.')
    }
  }

  return (
    <div className={estilos.registrar}>
      <ElegirSobrantes
        materiales={materiales.data ?? []}
        cargando={materiales.isLoading}
        error={materiales.error ? (materiales.error as Error).message : null}
        onCambio={setValor}
      />
      {error && <p className={estilos.error}>{error}</p>}
      <div className={estilos.botones}>
        <button type="button" className={estilos.botonTexto} onClick={onListo}>
          Cancelar
        </button>
        <button
          type="button"
          className={estilos.boton}
          disabled={valor === null || reportar.isPending}
          onClick={() => void guardar()}
        >
          {reportar.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  )
}
